import { DownloadOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Channel,
  DOWNLOAD_IDEAS,
  GET_CHANNEL_BY_ID,
} from '@frontend/data-access';
import { Button, DatePicker, Divider, Form, Modal } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import router from 'next/router';
import { useState } from 'react';

const getYearMonth = (date: Dayjs) => date.year() * 12 + date.month();

// Disabled dates more than 1 year from the selected date
const disabled1YearDate: DatePickerProps['disabledDate'] = (
  current,
  { from, type }
) => {
  if (from) {
    const minDate = from.add(-364, 'days'); // 365 days total (including the from date)
    const maxDate = from.add(364, 'days');

    switch (type) {
      case 'year':
        return (
          current.year() < minDate.year() || current.year() > maxDate.year()
        );

      case 'month':
        return (
          getYearMonth(current) < getYearMonth(minDate) ||
          getYearMonth(current) > getYearMonth(maxDate)
        );

      default:
        return Math.abs(current.diff(from, 'days')) >= 365;
    }
  }

  return false;
};

const DownloadIdeasButton = () => {
  const { t } = useTranslation('common');
  const { query } = router;
  const channelId = query['id'];
  const [form] = Form.useForm();
  const [isOpenDownload, setIsOpenDownload] = useState(false);
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const { data: channelData } = useQuery<{
    channel: Channel;
  }>(GET_CHANNEL_BY_ID, { variables: { id: channelId } });
  const [downloadIdeas, { loading: loadingDownload }] = useLazyQuery<{
    downloadChannelIdeaList: string;
  }>(DOWNLOAD_IDEAS, {
    onCompleted(data) {
      const startDate = dateRange
        ? dayjs(dateRange[0]).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const endDate = dateRange
        ? dayjs(dateRange[1]).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const filename = `ideas_${startDate}_${endDate}.csv`;
      const decodedString = Buffer.from(
        data.downloadChannelIdeaList,
        'base64'
      ).toString('utf8');
      const blob = new Blob([decodedString], {
        type: 'text/csv',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  });

  const handleDownload = (values: { dateRange: [Date, Date] }) => {
    setDateRange(values.dateRange);
    downloadIdeas({
      variables: {
        channelId,
        startDate: dayjs(values.dateRange[0]).format('YYYY-MM-DD'),
        endDate: dayjs(values.dateRange[1]).format('YYYY-MM-DD'),
      },
    });
  };

  return (
    <div>
      <Button
        shape="round"
        icon={<DownloadOutlined />}
        type="text"
        onClick={() => setIsOpenDownload(true)}
        data-test="download-ideas-btn"
      >
        {t('download_ideas')}
      </Button>
      <Modal
        title={`${t('download_ideas')} - ${channelData?.channel.title} ${t(
          'channel'
        ).toLowerCase()}`}
        open={isOpenDownload}
        onCancel={() => setIsOpenDownload(false)}
        footer={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleDownload}
          className="mt-5"
        >
          <Form.Item
            label={t('select_date_range')}
            name="dateRange"
            extra={t('download_ideas_date_range_description')}
            rules={[
              {
                required: true,
                message: t('date_range_required'),
              },
            ]}
          >
            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              placeholder={[t('start_date'), t('end_date')]}
              disabledDate={disabled1YearDate}
            />
          </Form.Item>
        </Form>
        <Divider />
        <div className="flex justify-between">
          <Button
            key="cancel"
            shape="round"
            onClick={() => setIsOpenDownload(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            key="download"
            type="primary"
            shape="round"
            onClick={() => {
              form.submit();
            }}
            loading={loadingDownload}
          >
            {t('download')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DownloadIdeasButton;
